<script lang="ts">
    import cn from 'classnames';
    import { onMount, tick } from 'svelte';

    import { onOutsideClick } from '@actions/on-outside-click';
    import { portal } from '@components/actions/portal';
    import { IconName, SvgIcon } from '@uikit/svg-icon';
    import { isFunction } from '@utils/type/checker';

    import type { Props } from './types';
    import { Position, TagName, Triggers, VerticalPosition } from './types';

    import s from './dropdown.module.css';

    export let isOpen: Props['isOpen'] = false;
    export let classes: Props['classes'] = '';
    export let dropdownClasses: Props['dropdownClasses'] = '';
    export let onOpen: Props['onOpen'] = null;
    export let onClose: Props['onClose'] = null;
    export let position: Props['position'] = Position.right;
    export let verticalPosition: Props['verticalPosition'] = VerticalPosition.down;
    export let triggerBy: Props['triggerBy'] = Triggers.click;
    export let disabled: Props['disabled'] = false;
    export let tagName: Props['tagName'] = TagName.div;
    export let delayMs: Props['delayMs'] = 0;
    export let testId: string | undefined = undefined;
    export let withToggle = false;
    export let isTriggerWidth = false;

    let dropdownElement: HTMLDivElement;
    let triggerElement: HTMLDivElement;
    let isMeasuring = false;
    let dropdownLeft = 'auto';
    let dropdownTop = 'auto';
    let dropdownRight = 'auto';
    let dropdownBottom = 'auto';
    let isInitDropdown = !delayMs;
    let hoverDelayTimer: number;
    let triggerWidth = 'auto';

    function checkVerticalPosition() {
        if (!dropdownElement || !triggerElement) return;
        isMeasuring = true;
        requestAnimationFrame(() => {
            const triggerRect = triggerElement.getBoundingClientRect();
            const dropdownHeight = dropdownElement.offsetHeight;
            const spaceBelow = window.innerHeight - triggerRect.bottom;
            const spaceAbove = triggerRect.top;
            const spacer = parseFloat(getComputedStyle(document.documentElement)
                .getPropertyValue('--xh-spacers-m')) || 8;

            if (isTriggerWidth) {
                triggerWidth = `${triggerRect.width}px`;
            }

            let vPosition: VerticalPosition;
            if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
                vPosition = VerticalPosition.up;
                dropdownBottom = `${window.innerHeight - triggerRect.top + spacer}px`;
                dropdownTop = 'auto';
            } else {
                vPosition = VerticalPosition.down;
                dropdownTop = `${triggerRect.bottom + spacer}px`;
                dropdownBottom = 'auto';
            }

            if (position === Position.right) {
                dropdownRight = `${window.innerWidth - triggerRect.right}px`;
                dropdownLeft = 'auto';
            } else {
                dropdownLeft = `${triggerRect.left}px`;
                dropdownRight = 'auto';
            }

            verticalPosition = vPosition;
            isMeasuring = false;
        });
    }

    export function open(): void {
        if (isOpen || disabled) return;
        isOpen = true;
        tick().then(() => {
            if (dropdownElement && triggerElement) {
                checkVerticalPosition();
            }
        });
        if (isFunction(onOpen)) onOpen();
    }

    export function close(): void {
        if (!isOpen || disabled) return;
        isOpen = false;
        if (isFunction(onClose)) onClose();
    }

    export function onKeyDown(e: KeyboardEvent) {
        if (disabled) return;

        switch (e.key) {
            case 'Enter':
            case 'Space':
                e.preventDefault();
                toggle();
                break;
            case 'Escape':
                if (isOpen) {
                    e.preventDefault();
                    close();
                }
                break;
            case 'ArrowDown':
            case 'ArrowUp':
                if (!isOpen) {
                    e.preventDefault();
                    open();
                }
                break;
        }
    }

    function onDropdownKeyDown(e: KeyboardEvent) {
        switch (e.key) {
            case 'Escape':
                close();
                break;
            case 'Enter':
            case 'Space':
                e.preventDefault();
                (e.target as HTMLElement).click();
                break;
            case 'ArrowUp':
            case 'ArrowDown':
            case 'Home':
            case 'End':
                e.preventDefault();
                break;
        }
    }

    function toggle(): void {
        if (isOpen) {
            close();
        } else {
            open();
        }
    }

    function onMouseEnter(): void {
        if (delayMs) {
            hoverDelayTimer = window.setTimeout(() => {
                isInitDropdown = true;
            }, delayMs);
        }
    }

    function onMouseLeave(): void {
        if (hoverDelayTimer) {
            window.clearTimeout(hoverDelayTimer);
        }
    }

    const handleItemClick = (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target?.closest('[data-close=no]')) {
            close();
        }
    };

    function getScrollableParents(element: HTMLElement): HTMLElement[] {
        const parents: HTMLElement[] = [];
        let current: HTMLElement | null = element;

        while (current && current !== document.body) {
            const style = window.getComputedStyle(current);
            const isScrollable = style.overflow === 'auto'
                || style.overflow === 'scroll' || style.overflowY === 'auto' || style.overflowY === 'scroll';
            if (isScrollable) {
                parents.push(current);
            }
            current = current.parentElement;
        }

        return parents;
    }

    function setupScrollListeners() {
        if (!triggerElement) return;

        const scrollableParents = getScrollableParents(triggerElement);
        scrollableParents.forEach((parent) => {
            parent.addEventListener('scroll', close, { passive: true });
        });

        return () => {
            scrollableParents.forEach((parent) => {
                parent.removeEventListener('scroll', close);
            });
        };
    }

        onMount(() => {
            const cleanupScrollListeners = setupScrollListeners();

            return () => {
                if (cleanupScrollListeners) {
                    cleanupScrollListeners();
                }
            };
        });
</script>

<svelte:window on:scroll={close} on:resize={checkVerticalPosition} />

<svelte:element
    this={tagName}
    on:mouseleave={onMouseLeave}
    use:onOutsideClick={triggerBy === Triggers.click
        ? {
            handler: close,
            exclude: [triggerElement, dropdownElement],
        }
        : null
    }
    class={cn(s['xh-dropdown'], classes)}
    role="menu"
    tabindex="0"
    aria-controls={isOpen ? 'dropdown-menu' : undefined}
>
    <div
        bind:this={triggerElement}
        class={cn(s.trigger, s[`on-${triggerBy}`], { [s.active]: isOpen })}
        on:mouseenter={onMouseEnter}
        on:click={triggerBy === Triggers.click ? toggle : null}
        role="button"
        tabindex="0"
        on:keydown={onKeyDown}
    >
        <slot name="trigger" />
        {#if withToggle}
            <SvgIcon classes={s.toggle} rotationAngle={isOpen ? 180 : 0} icon={IconName.ArrowDownShort} />
        {/if}
    </div>
</svelte:element>

{#if isInitDropdown}
    <div
        use:portal
        bind:this={dropdownElement}
        id="dropdown-menu"
        class={cn(s.dropdown, dropdownClasses, s[`position-${position}`], s[`v-position-${verticalPosition}`], {
            [s.open]: isOpen,
            [s.blockAndHide]: isMeasuring,
        })}
        style:position="fixed"
        style:left={dropdownLeft}
        style:top={dropdownTop}
        style:right={dropdownRight}
        style:bottom={dropdownBottom}
        style:width={triggerWidth}
        on:click={handleItemClick}
        on:keydown={onDropdownKeyDown}
        data-testid={testId}
        role="menu"
        tabindex="0"
    >
        <slot name="dropdown" />
    </div>
{/if}
